/**
 * GARCHEducationPanel - Interactive GARCH Education and Help Component
 *
 * This component provides comprehensive educational content about GARCH modeling,
 * making advanced volatility concepts accessible to portfolio management users.
 *
 * Key Features:
 * - Interactive explanations of GARCH concepts
 * - Visual demonstrations of volatility clustering
 * - Practical examples and use cases
 * - Step-by-step model explanation
 * - Links to further reading and research
 * - Real-time parameter impact visualization
 *
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 * @since 2.1.0
 */

import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import { GARCHRateCalculationIntegration } from '../../utils/garchModeling';

/**
 * Props für die GARCHEducationPanel-Komponente
 */
interface GARCHEducationPanelProps {
  /** Optional: show advanced technical content */
  showAdvancedContent?: boolean;
}

/**
 * GARCHEducationPanel - Main educational component
 */
export const GARCHEducationPanel: React.FC<GARCHEducationPanelProps> = ({
  showAdvancedContent = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showInteractiveDemo, setShowInteractiveDemo] = useState(false);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Get GARCH analysis for real examples
  const garchAnalysis = React.useMemo(() => {
    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
    } catch {
      return null;
    }
  }, []);

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Introduction */}
      <Container>
        <Header variant="h2">GARCH Volatilitäts-Modellierung verstehen</Header>
        <SpaceBetween direction="vertical" size="m">
          <Alert type="info" statusIconAriaLabel="Info">
            <strong>Was ist GARCH?</strong> GARCH (Generalized Autoregressive Conditional
            Heteroskedasticity) ist ein statistisches Modell, das die zeitvariable Volatilität von
            Finanzmärkten realitätsnah abbildet und für präzisere Risiko-Berechnungen sorgt.
          </Alert>

          <TextContent>
            <p>
              Traditionelle Finanzmodelle nehmen oft konstante Volatilität an - das entspricht aber
              nicht der Realität. In echten Märkten wechseln sich ruhige und turbulente Perioden ab,
              und große Marktbewegungen treten häufig gehäuft auf. GARCH-Modelle erfassen diese
              Muster.
            </p>
          </TextContent>
        </SpaceBetween>
      </Container>

      {/* Core Concepts */}
      <Container header={<Header variant="h3">Grundkonzepte der GARCH-Modellierung</Header>}>
        <SpaceBetween direction="vertical" size="l">
          {/* Volatility Clustering */}
          <ExpandableSection
            headerText="Volatilitäts-Clustering"
            expanded={expandedSections.has('clustering')}
            onChange={() => toggleSection('clustering')}
          >
            <SpaceBetween direction="vertical" size="m">
              <ColumnLayout columns={2} borders="vertical">
                <Box>
                  <Header variant="h3">Das Phänomen</Header>
                  <TextContent>
                    <p>
                      <strong>"Hohe Volatilität folgt auf hohe Volatilität"</strong> - Diese
                      Beobachtung stammt von Benoit Mandelbrot (1963) und ist ein zentrales Merkmal
                      von Finanzmärkten.
                    </p>
                    <ul>
                      <li>Krisen-Perioden mit aufeinanderfolgenden großen Bewegungen</li>
                      <li>Ruhige Phasen mit konstant niedrigen Schwankungen</li>
                      <li>Unvorhersagbare Übergänge zwischen den Regimen</li>
                    </ul>
                  </TextContent>
                </Box>
                <Box>
                  <Header variant="h3">Praktisches Beispiel</Header>
                  <Alert type="warning" statusIconAriaLabel="Beispiel">
                    <strong>Finanzkrise 2008:</strong> Nach dem ersten großen Markteinbruch folgten
                    wochenlang weitere starke Schwankungen. Die Volatilität blieb über Monate
                    erhöht, bevor sich die Märkte wieder beruhigten.
                  </Alert>
                  {garchAnalysis && (
                    <Box variant="small" color="text-body-secondary">
                      <p>
                        <strong>Reale Daten:</strong> Der MSCI World zeigt eine ARCH-Effekt-Stärke
                        von
                        {garchAnalysis.garchParameters.archEffect}, was mittleres Clustering
                        anzeigt.
                      </p>
                    </Box>
                  )}
                </Box>
              </ColumnLayout>
            </SpaceBetween>
          </ExpandableSection>

          {/* Leverage Effect */}
          <ExpandableSection
            headerText="Leverage-Effekt (Asymmetrische Volatilität)"
            expanded={expandedSections.has('leverage')}
            onChange={() => toggleSection('leverage')}
          >
            <SpaceBetween direction="vertical" size="m">
              <ColumnLayout columns={2} borders="vertical">
                <Box>
                  <Header variant="h3">Das Phänomen</Header>
                  <TextContent>
                    <p>
                      <strong>
                        Negative Marktbewegungen erhöhen die Volatilität stärker als positive.
                      </strong>
                      Dies wurde erstmals von Fischer Black (1976) dokumentiert.
                    </p>
                    <ul>
                      <li>Marktcrashes führen zu erhöhter Nervosität</li>
                      <li>Positive Nachrichten beruhigen die Märkte</li>
                      <li>Asymmetrische Reaktion auf gute vs. schlechte News</li>
                    </ul>
                  </TextContent>
                </Box>
                <Box>
                  <Header variant="h3">Warum passiert das?</Header>
                  <TextContent>
                    <p>
                      <strong>Psychologische Faktoren:</strong>
                    </p>
                    <ul>
                      <li>
                        <strong>Loss Aversion:</strong> Verluste schmerzen mehr als Gewinne erfreuen
                      </li>
                      <li>
                        <strong>Herdenverhalten:</strong> Panikverkäufe verstärken sich selbst
                      </li>
                      <li>
                        <strong>Leverage:</strong> Fallende Kurse zwingen zum Verkauf
                      </li>
                    </ul>
                  </TextContent>
                  {garchAnalysis && (
                    <Box variant="small" color="text-body-secondary">
                      <p>
                        <strong>Messung:</strong> Leverage-Parameter von{' '}
                        {garchAnalysis.garchParameters.leverageEffect}
                        zeigt moderate Asymmetrie in den historischen Daten.
                      </p>
                    </Box>
                  )}
                </Box>
              </ColumnLayout>
            </SpaceBetween>
          </ExpandableSection>

          {/* Mean Reversion */}
          <ExpandableSection
            headerText="Mean Reversion (Rückkehr zum Durchschnitt)"
            expanded={expandedSections.has('reversion')}
            onChange={() => toggleSection('reversion')}
          >
            <SpaceBetween direction="vertical" size="m">
              <ColumnLayout columns={2} borders="vertical">
                <Box>
                  <Header variant="h3">Das Konzept</Header>
                  <TextContent>
                    <p>
                      <strong>
                        Volatilität kehrt langfristig zu ihrem historischen Durchschnitt zurück.
                      </strong>
                      Extremphasen sind temporär, nicht permanent.
                    </p>
                    <ul>
                      <li>Krisen-Volatilität normalisiert sich wieder</li>
                      <li>Zu niedrige Volatilität steigt wieder an</li>
                      <li>Langfristige Stabilität trotz kurzfristiger Schwankungen</li>
                    </ul>
                  </TextContent>
                </Box>
                <Box>
                  <Header variant="h3">Praktische Bedeutung</Header>
                  <Alert type="success" statusIconAriaLabel="Bedeutung">
                    <strong>Für Portfolio-Management:</strong> Extreme Volatilitätsphasen sind
                    vorübergehend. Langfristige Strategien können von diesem Wissen profitieren.
                  </Alert>
                  {garchAnalysis && (
                    <Box variant="small" color="text-body-secondary">
                      <p>
                        <strong>Persistenz:</strong> Mit{' '}
                        {garchAnalysis.garchParameters.volatilityPersistence}
                        Persistenz kehrt die Volatilität relativ schnell zum Durchschnitt zurück.
                      </p>
                    </Box>
                  )}
                </Box>
              </ColumnLayout>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      {/* GARCH vs Standard Models */}
      <Container header={<Header variant="h3">GARCH vs. Traditionelle Modelle</Header>}>
        <ColumnLayout columns={2} borders="vertical">
          <Box>
            <Header variant="h3">
              <Badge color="red">Standard Volatilitäts-Modelle</Badge>
            </Header>
            <SpaceBetween direction="vertical" size="s">
              <TextContent>
                <p>
                  <strong>Annahmen:</strong>
                </p>
                <ul>
                  <li>Konstante Volatilität über die Zeit</li>
                  <li>Unabhängige, normalverteilte Renditen</li>
                  <li>Symmetrische Marktreaktionen</li>
                  <li>Keine Berücksichtigung von Marktregimen</li>
                </ul>
              </TextContent>

              <Alert type="error" statusIconAriaLabel="Probleme">
                <strong>Probleme:</strong> Unterschätzt Krisen-Risiken, ignoriert
                Volatilitäts-Clustering, führt zu falschen Risiko-Berechnungen in turbulenten
                Zeiten.
              </Alert>
            </SpaceBetween>
          </Box>

          <Box>
            <Header variant="h3">
              <Badge color="green">GARCH Volatilitäts-Modelle</Badge>
            </Header>
            <SpaceBetween direction="vertical" size="s">
              <TextContent>
                <p>
                  <strong>Realistische Features:</strong>
                </p>
                <ul>
                  <li>Zeitvariable, adaptive Volatilität</li>
                  <li>Volatilitäts-Clustering Effekte</li>
                  <li>Asymmetrische Leverage-Effekte</li>
                  <li>Mean Reversion zu langfristigem Durchschnitt</li>
                </ul>
              </TextContent>

              <Alert type="success" statusIconAriaLabel="Vorteile">
                <strong>Vorteile:</strong> Realistischere Risiko-Schätzung, bessere
                Krisen-Simulation, präzisere Portfolio-Optimierung.
              </Alert>
            </SpaceBetween>
          </Box>
        </ColumnLayout>
      </Container>

      {/* Interactive Demo */}
      <Container header={<Header variant="h3">Interaktive Demonstration</Header>}>
        <SpaceBetween direction="vertical" size="m">
          <Button
            variant={showInteractiveDemo ? 'normal' : 'primary'}
            onClick={() => setShowInteractiveDemo(!showInteractiveDemo)}
          >
            {showInteractiveDemo ? 'Demo verbergen' : 'Interaktive Demo starten'}
          </Button>

          {showInteractiveDemo && garchAnalysis && (
            <Alert type="info" statusIconAriaLabel="Demo">
              <SpaceBetween direction="vertical" size="m">
                <Header variant="h3">Live GARCH Parameter - MSCI World Index</Header>
                <ColumnLayout columns={3} borders="horizontal">
                  <Box>
                    <Box variant="awsui-key-label">Volatilitäts-Persistenz (β)</Box>
                    <Box variant="awsui-value-large">
                      {garchAnalysis.garchParameters.volatilityPersistence}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Wie stark wirkt vergangene Volatilität auf heute
                    </Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">ARCH-Effekt (α)</Box>
                    <Box variant="awsui-value-large">
                      {garchAnalysis.garchParameters.archEffect}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Stärke des Volatilitäts-Clustering
                    </Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Leverage-Effekt (θ)</Box>
                    <Box variant="awsui-value-large">
                      {garchAnalysis.garchParameters.leverageEffect}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Asymmetrie: negative Schocks vs. positive
                    </Box>
                  </Box>
                </ColumnLayout>

                <TextContent>
                  <p>
                    <strong>Interpretation:</strong> Diese Parameter wurden aus{' '}
                    {garchAnalysis.dataPoints}
                    historischen Datenpunkten geschätzt. Die Volatilitäts-Persistenz von
                    {garchAnalysis.garchParameters.volatilityPersistence} bedeutet, dass etwa{' '}
                    {(
                      parseFloat(garchAnalysis.garchParameters.volatilityPersistence) * 100
                    ).toFixed(0)}
                    % der heutigen Volatilität von gestern "übertragen" wird.
                  </p>
                </TextContent>
              </SpaceBetween>
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {/* Practical Applications */}
      <Container
        header={<Header variant="h3">Praktische Anwendungen im Portfolio-Management</Header>}
      >
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2} borders="vertical">
            <Box>
              <Header variant="h3">Risiko-Management</Header>
              <TextContent>
                <ul>
                  <li>
                    <strong>Value-at-Risk:</strong> Präzisere Risiko-Schätzungen
                  </li>
                  <li>
                    <strong>Stress-Testing:</strong> Realistische Krisen-Szenarien
                  </li>
                  <li>
                    <strong>Portfolio-Optimierung:</strong> Bessere Diversifikation
                  </li>
                  <li>
                    <strong>Dynamische Hedging:</strong> Anpassung an Marktregime
                  </li>
                </ul>
              </TextContent>
            </Box>

            <Box>
              <Header variant="h3">Strategische Planung</Header>
              <TextContent>
                <ul>
                  <li>
                    <strong>Asset Allocation:</strong> Berücksichtigung von Volatilitäts-Regimen
                  </li>
                  <li>
                    <strong>Entnahme-Strategien:</strong> Anpassung an Marktbedingungen
                  </li>
                  <li>
                    <strong>Rebalancing:</strong> Timing basierend auf Volatilitäts-Niveau
                  </li>
                  <li>
                    <strong>Krisen-Vorbereitung:</strong> Antizipation von Clustering
                  </li>
                </ul>
              </TextContent>
            </Box>
          </ColumnLayout>

          <Alert type="success" statusIconAriaLabel="Portfolio-3Topf">
            <strong>In Portfolio-3Topf:</strong> GARCH-Modellierung hilft bei der realistischen
            Simulation von Entnahme-Strategien, indem Volatilitäts-Clustering und Leverage-Effekte
            berücksichtigt werden. Dies führt zu robusteren Strategien und besserer Krisenresistenz.
          </Alert>
        </SpaceBetween>
      </Container>

      {/* Advanced Content */}
      {showAdvancedContent && (
        <Container header={<Header variant="h3">Technische Details (Erweitert)</Header>}>
          <SpaceBetween direction="vertical" size="m">
            <ExpandableSection
              headerText="Warum EGARCH? Vergleich der GARCH-Varianten"
              expanded={expandedSections.has('garch-variants')}
              onChange={() => toggleSection('garch-variants')}
            >
              <SpaceBetween direction="vertical" size="m">
                <TextContent>
                  <p>
                    <strong>Es gibt verschiedene GARCH-Varianten</strong> - wir haben EGARCH
                    (Exponential GARCH) für Portfolio-3Topf gewählt, weil es die realistischsten
                    Eigenschaften für Finanzmärkte bietet.
                  </p>
                </TextContent>

                <ColumnLayout columns={1} borders="horizontal">
                  <Box>
                    <Box variant="h4">📈 Standard GARCH(1,1)</Box>
                    <TextContent>
                      <p>
                        <strong>Vorteile:</strong>
                      </p>
                      <ul>
                        <li>Einfach zu verstehen und implementieren</li>
                        <li>Schnelle Berechnung</li>
                        <li>Robuste Parameter-Schätzung</li>
                      </ul>
                      <p>
                        <strong>Nachteile:</strong>
                      </p>
                      <ul>
                        <li>
                          🔴 <strong>Keine Asymmetrie:</strong> Positive und negative Schocks haben
                          gleiche Wirkung
                        </li>
                        <li>
                          🔴 <strong>Unrealistisch:</strong> Ignoriert den Leverage-Effekt völlig
                        </li>
                        <li>
                          🔴 <strong>Volatilitäts-Constraints:</strong> Parameter müssen strenge
                          Bedingungen erfüllen
                        </li>
                      </ul>
                    </TextContent>
                  </Box>
                </ColumnLayout>

                <ColumnLayout columns={1} borders="horizontal">
                  <Box>
                    <Box variant="h4">📊 GJR-GARCH</Box>
                    <TextContent>
                      <p>
                        <strong>Vorteile:</strong>
                      </p>
                      <ul>
                        <li>Erfasst Asymmetrie zwischen positiven/negativen Schocks</li>
                        <li>Relativ einfache Erweiterung des Standard-GARCH</li>
                        <li>Gute empirische Performance</li>
                      </ul>
                      <p>
                        <strong>Nachteile:</strong>
                      </p>
                      <ul>
                        <li>
                          🟡 <strong>Begrenzte Flexibilität:</strong> Asymmetrie nur über
                          Schwellenwerte
                        </li>
                        <li>
                          🟡 <strong>Parameter-Constraints:</strong> Komplexe
                          Nicht-Negativitäts-Bedingungen
                        </li>
                        <li>
                          🟡 <strong>Stabilität:</strong> Kann bei extremen Parametern instabil
                          werden
                        </li>
                      </ul>
                    </TextContent>
                  </Box>
                </ColumnLayout>

                <ColumnLayout columns={1} borders="horizontal">
                  <Box>
                    <Box variant="h4">🎯 EGARCH (Unsere Wahl)</Box>
                    <TextContent>
                      <p>
                        <strong>Warum EGARCH die beste Wahl ist:</strong>
                      </p>
                      <ul>
                        <li>
                          ✅ <strong>Automatische Asymmetrie:</strong> Modelliert Leverage-Effekt
                          natürlich
                        </li>
                        <li>
                          ✅ <strong>Keine Parameter-Constraints:</strong> Logarithmische Form
                          garantiert positive Volatilität
                        </li>
                        <li>
                          ✅ <strong>Robustheit:</strong> Numerisch stabiler bei extremen
                          Marktbedingungen
                        </li>
                        <li>
                          ✅ <strong>Flexibilität:</strong> Kann verschiedene Asymmetrie-Grade
                          erfassen
                        </li>
                        <li>
                          ✅ <strong>Empirische Überlegenheit:</strong> Bessere Anpassung an
                          Finanzmarkt-Daten
                        </li>
                      </ul>

                      <p>
                        <strong>Spezifische Vorteile für Portfolio-Management:</strong>
                      </p>
                      <ul>
                        <li>
                          <strong>Krisen-Simulation:</strong> Realistische Modellierung von
                          Marktcrashs
                        </li>
                        <li>
                          <strong>Risiko-Schätzung:</strong> Präzisere VaR-Berechnungen in
                          turbulenten Zeiten
                        </li>
                        <li>
                          <strong>Entnahme-Strategien:</strong> Bessere Simulation von
                          Bear-Market-Szenarien
                        </li>
                        <li>
                          <strong>Diversifikation:</strong> Realistischere Korrelations-Dynamiken
                        </li>
                      </ul>
                    </TextContent>

                    <Alert type="success" statusIconAriaLabel="EGARCH Vorteile">
                      <strong>Kernvorteil für Portfolio-3Topf:</strong> EGARCH erfasst den wichtigen
                      Unterschied zwischen "guten" und "schlechten" Marktphasen. Da Krisen oft
                      asymmetrisch verlaufen (schnelle Abstürze, langsame Erholungen), führt EGARCH
                      zu realistischeren Worst-Case-Szenarien für Ihre Entnahme-Strategien.
                    </Alert>
                  </Box>
                </ColumnLayout>

                <ColumnLayout columns={1} borders="horizontal">
                  <Box>
                    <Box variant="h4">🔬 Empirische Evidenz</Box>
                    <TextContent>
                      <p>
                        <strong>Warum die wissenschaftliche Literatur EGARCH bevorzugt:</strong>
                      </p>
                      <ul>
                        <li>
                          <strong>Nelson (1991):</strong> Erste Dokumentation der logarithmischen
                          Vorteile
                        </li>
                        <li>
                          <strong>Bollerslev et al. (1992):</strong> Überlegene Performance bei
                          Stock-Index-Daten
                        </li>
                        <li>
                          <strong>Pagan & Schwert (1990):</strong> Bessere Out-of-Sample Vorhersagen
                        </li>
                        <li>
                          <strong>Moderne Studien:</strong> Konsistent bessere Anpassung an Emerging
                          Markets
                        </li>
                      </ul>

                      <p>
                        <strong>Portfolio-3Topf Validierung:</strong>
                      </p>
                      <ul>
                        <li>Backtesting gegen 30+ Jahre MSCI World Daten</li>
                        <li>Vergleich mit Standard-GARCH in Crisis-Perioden (2008, 2020)</li>
                        <li>Monte-Carlo Validierung der Parameter-Stabilität</li>
                        <li>Cross-Validation gegen andere Asset-Klassen</li>
                      </ul>
                    </TextContent>
                  </Box>
                </ColumnLayout>
              </SpaceBetween>
            </ExpandableSection>

            <ExpandableSection
              headerText="EGARCH(1,1) Modell-Spezifikation"
              expanded={expandedSections.has('technical')}
              onChange={() => toggleSection('technical')}
            >
              <TextContent>
                <p>
                  <strong>Exponential GARCH (1,1) Gleichung:</strong>
                </p>
                <Box variant="code">log(σₜ²) = ω + β·log(σₜ₋₁²) + α·g(εₜ₋₁)</Box>
                <p>
                  <strong>wobei g(ε) = θ·ε + γ·(|ε| - E[|ε|]):</strong>
                </p>
                <Box variant="code">g(εₜ₋₁) = θ·εₜ₋₁ + γ·(|εₜ₋₁| - √(2/π))</Box>
                <p>Wobei:</p>
                <ul>
                  <li>
                    <strong>σₜ²:</strong> Bedingte Varianz zum Zeitpunkt t
                  </li>
                  <li>
                    <strong>ω:</strong> Konstanter Term
                  </li>
                  <li>
                    <strong>α:</strong> ARCH-Effekt (Volatilitäts-Clustering)
                  </li>
                  <li>
                    <strong>θ:</strong> Leverage-Effekt (signierter Term: negative Schocks
                    erhöhen Volatilität stärker)
                  </li>
                  <li>
                    <strong>γ:</strong> Magnituden-Effekt (Volatilitäts-Clustering: große
                    Schocks erhöhen Folge-Volatilität)
                  </li>
                  <li>
                    <strong>β:</strong> Persistenz-Parameter
                  </li>
                  <li>
                    <strong>εₜ:</strong> Standardisierte Residuen
                  </li>
                </ul>
              </TextContent>
            </ExpandableSection>

            <ExpandableSection
              headerText="Kalibrierungs-Methoden"
              expanded={expandedSections.has('calibration')}
              onChange={() => toggleSection('calibration')}
            >
              <TextContent>
                <p>
                  <strong>Maximum Likelihood Estimation (MLE):</strong>
                </p>
                <ul>
                  <li>Optimierung der Log-Likelihood Funktion</li>
                  <li>Numerische Optimierung mit BFGS Algorithmus</li>
                  <li>Robuste Standardfehler-Schätzung</li>
                  <li>Konvergenz-Diagnostik</li>
                </ul>

                <p>
                  <strong>Daten-Vorbereitung:</strong>
                </p>
                <ul>
                  <li>Log-Renditen aus Preis-Zeitreihen</li>
                  <li>Outlier-Behandlung und Bereinigung</li>
                  <li>Missing Data Interpolation</li>
                  <li>Stationaritäts-Tests</li>
                </ul>
              </TextContent>
            </ExpandableSection>
          </SpaceBetween>
        </Container>
      )}

      {/* Further Reading */}
      <Container header={<Header variant="h3">Weitere Ressourcen</Header>}>
        <TextContent>
          <p>
            <strong>Wissenschaftliche Grundlagen:</strong>
          </p>
          <ul>
            <li>
              Engle, R.F. (1982): "Autoregressive Conditional Heteroscedasticity with Estimates of
              the Variance of United Kingdom Inflation"
            </li>
            <li>
              Bollerslev, T. (1986): "Generalized Autoregressive Conditional Heteroskedasticity"
            </li>
            <li>
              Nelson, D.B. (1991): "Conditional Heteroskedasticity in Asset Returns: A New Approach"
            </li>
          </ul>

          <p>
            <strong>Praktische Implementierung:</strong>
          </p>
          <ul>
            <li>Portfolio-3Topf GARCH Integration Guide (docs/GARCH_INTEGRATION_GUIDE.md)</li>
            <li>Technische Dokumentation der Parameter-Kalibrierung</li>
            <li>Validierungs-Tests und Performance-Benchmarks</li>
          </ul>
        </TextContent>
      </Container>
    </SpaceBetween>
  );
};
